import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  Radar, 
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { 
  ArrowLeft, 
  Trophy, 
  Target, 
  TrendingUp, 
  TrendingDown,
  Clock,
  MessageSquare,
  Mic,
  Eye,
  FileText,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  ChevronRight,
  Play,
  Share2,
  Download,
  Calendar,
  BarChart3
} from 'lucide-react';

const EvolveFeedback = () => {
  const { sessionId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [slides, setSlides] = useState([]);

  useEffect(() => {
    fetchFeedbackData();
  }, [sessionId]);

  const fetchFeedbackData = async () => {
    try {
      setLoading(true);
      
      const [sessionRes, feedbackRes, slidesRes] = await Promise.all([
        api.get(`/evolve/sessions/${sessionId}`),
        api.get(`/evolve/sessions/${sessionId}/feedback`),
        api.get(`/evolve/sessions/${sessionId}/slides`)
      ]);
      
      setSession(sessionRes.data);
      setFeedback(feedbackRes.data);
      setSlides(slidesRes.data.slides || []);
      
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#22c55e'; // emerald
    if (score >= 60) return '#f59e0b'; // amber
    return '#ef4444'; // rose
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Great';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Work';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Prepare radar chart data - using correct field names
  const radarData = feedback ? [
    { category: 'Content', value: feedback.content_clarity || 0, fullMark: 100 },
    { category: 'Structure', value: feedback.structure_flow || 0, fullMark: 100 },
    { category: 'Verbal', value: feedback.verbal_communication || 0, fullMark: 100 },
    { category: 'Q&A', value: feedback.qa_handling || 0, fullMark: 100 },
    { category: 'Presence', value: feedback.confidence_presence || 0, fullMark: 100 },
    { category: 'Slides', value: feedback.slide_effectiveness || 0, fullMark: 100 },
    { category: 'Engagement', value: feedback.engagement_level || 0, fullMark: 100 },
  ] : [];

  const categoryIcons = {
    content_clarity: FileText,
    structure_flow: BarChart3,
    verbal_communication: Mic,
    qa_handling: MessageSquare,
    confidence_presence: Trophy,
    slide_effectiveness: Eye,
    engagement_level: Target
  };

  const categoryDescriptions = {
    content_clarity: 'How clearly you explained concepts and organized information',
    structure_flow: 'Logical flow and organization of your presentation',
    verbal_communication: 'Voice modulation, pacing, and articulation',
    qa_handling: 'Quality and completeness of Q&A responses',
    confidence_presence: 'Displayed confidence and composure',
    slide_effectiveness: 'Quality and effectiveness of visual aids',
    engagement_level: 'Ability to capture and maintain audience attention'
  };

  // Helper function to get array items from feedback
  const getStrengths = () => {
    if (Array.isArray(feedback.strengths)) return feedback.strengths;
    return [];
  };

  const getImprovements = () => {
    if (Array.isArray(feedback.improvements)) return feedback.improvements;
    if (Array.isArray(feedback.areas_to_improve)) return feedback.areas_to_improve;
    return [];
  };

  // Helper to get improvement plan text
  const getImprovementPlanText = () => {
    if (typeof feedback.improvement_plan === 'string') return feedback.improvement_plan;
    if (typeof feedback.improvement_plan === 'object' && feedback.improvement_plan) {
      // Convert object to readable text
      const sections = [];
      for (const [key, value] of Object.entries(feedback.improvement_plan)) {
        const title = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        if (Array.isArray(value)) {
          sections.push(`${title}:\n${value.map(v => `• ${v}`).join('\n')}`);
        } else if (typeof value === 'string') {
          sections.push(`${title}: ${value}`);
        }
      }
      return sections.join('\n\n');
    }
    return null;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-8">
          <div className="max-w-6xl mx-auto space-y-6">
            <Skeleton className="h-8 w-64" />
            <div className="grid lg:grid-cols-3 gap-6">
              <Skeleton className="h-96 lg:col-span-2" />
              <Skeleton className="h-96" />
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!feedback) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-8">
          <div className="max-w-6xl mx-auto text-center py-16">
            <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Feedback Not Available
            </h2>
            <p className="text-slate-600 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              This session has not been analyzed yet.
            </p>
            <Button onClick={() => navigate('/evolve')} className="bg-[#E07A5F] hover:bg-[#D06A4F]">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/evolve')}
              className="mb-4 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {session?.topic_name}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Analyzed
                  </Badge>
                  <span className="text-sm text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Calendar className="h-4 w-4 inline mr-1" />
                    {formatDate(feedback.created_at)}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  className="border-slate-300"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/evolve/sessions/${sessionId}/feedback/pdf`, '_blank')}
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button variant="outline" className="border-slate-300">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
                <Button 
                  onClick={() => navigate(`/evolve/live/${sessionId}`)}
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Practice Again
                </Button>
              </div>
            </div>
          </div>

          {/* Main Score Card */}
          <Card className="bg-white border-slate-200 mb-8 overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Score Display */}
              <div className="p-8 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-white border-b lg:border-b-0 lg:border-r border-slate-100">
                <p className="text-sm text-slate-500 mb-2 uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Overall Score
                </p>
                <div className="relative">
                  <svg className="w-48 h-48 transform -rotate-90">
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke="#e2e8f0"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="96"
                      cy="96"
                      r="88"
                      stroke={getScoreColor(feedback.overall_score)}
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(feedback.overall_score / 100) * 553} 553`}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span 
                      className="text-5xl font-bold"
                      style={{ color: getScoreColor(feedback.overall_score), fontFamily: 'Outfit, sans-serif' }}
                    >
                      {feedback.overall_score}
                    </span>
                    <span className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {getScoreLabel(feedback.overall_score)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Radar Chart */}
              <div className="p-8">
                <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Performance Overview
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis 
                      dataKey="category" 
                      tick={{ fill: '#64748b', fontSize: 12, fontFamily: 'Manrope, sans-serif' }}
                    />
                    <PolarRadiusAxis 
                      angle={30} 
                      domain={[0, 100]} 
                      tick={{ fill: '#94a3b8', fontSize: 10 }}
                    />
                    <Radar
                      name="Score"
                      dataKey="value"
                      stroke="#E07A5F"
                      fill="#E07A5F"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>

          {/* Detailed Scores */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Category Scores */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-slate-200 h-full">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Category Breakdown</CardTitle>
                  <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Detailed scores across all 7 evaluation categories
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { key: 'content_clarity', label: 'Content Clarity', score: feedback.content_clarity },
                    { key: 'structure_flow', label: 'Structure & Flow', score: feedback.structure_flow },
                    { key: 'verbal_communication', label: 'Verbal Communication', score: feedback.verbal_communication },
                    { key: 'qa_handling', label: 'Q&A Handling', score: feedback.qa_handling },
                    { key: 'confidence_presence', label: 'Confidence & Presence', score: feedback.confidence_presence },
                    { key: 'slide_effectiveness', label: 'Slide Effectiveness', score: feedback.slide_effectiveness },
                    { key: 'engagement_level', label: 'Engagement Level', score: feedback.engagement_level },
                  ].map(({ key, label, score }) => {
                    const Icon = categoryIcons[key];
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-slate-100">
                              <Icon className="h-4 w-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {label}
                              </p>
                              <p className="text-xs text-slate-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {categoryDescriptions[key]}
                              </p>
                            </div>
                          </div>
                          <span 
                            className="text-lg font-bold"
                            style={{ color: getScoreColor(score || 0), fontFamily: 'Outfit, sans-serif' }}
                          >
                            {score || 0}%
                          </span>
                        </div>
                        <Progress 
                          value={score || 0} 
                          className="h-2"
                          style={{ 
                            '--progress-background': getScoreColor(score || 0) 
                          }}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Key Insights */}
            <div className="space-y-6">
              {/* Strengths */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <TrendingUp className="h-5 w-5 text-emerald-500" />
                    Strengths
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {getStrengths().length > 0 ? getStrengths().map((strength, index) => (
                      <li key={`strength-${index}-${String(strength).substring(0, 20)}`} className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {String(strength)}
                      </li>
                    )) : (
                      <li className="text-sm text-slate-400">No specific strengths identified</li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              {/* Areas to Improve */}
              <Card className="bg-white border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Target className="h-5 w-5 text-amber-500" />
                    Areas to Improve
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {getImprovements().length > 0 ? getImprovements().map((area, index) => (
                      <li key={`improvement-${index}-${String(area).substring(0, 20)}`} className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        {String(area)}
                      </li>
                    )) : (
                      <li className="text-sm text-slate-400">No specific areas identified</li>
                    )}
                  </ul>
                </CardContent>
              </Card>

              {/* Quick Tips */}
              <Card className="bg-gradient-to-br from-[#E07A5F]/10 to-[#F2CC8F]/10 border-[#E07A5F]/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Lightbulb className="h-5 w-5 text-[#E07A5F]" />
                    Score Justification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {feedback.score_justification || feedback.overall_feedback || 'Keep practicing regularly to improve your presentation skills. Focus on one area at a time for better results.'}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Slide-by-Slide Feedback */}
          {feedback.slide_feedback && feedback.slide_feedback.length > 0 && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Slide-by-Slide Analysis</CardTitle>
                <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Detailed feedback for each slide in your presentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-4">
                    {feedback.slide_feedback.map((slideFb, index) => (
                      <div 
                        key={`slide-${slideFb.slide || slideFb.slide_number || index}`}
                        className="flex gap-4 p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        {/* Slide Thumbnail */}
                        <div className="w-32 h-20 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0">
                          {slides[index] ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${slides[index].url}`}
                              alt={`Slide ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <FileText className="h-8 w-8" />
                            </div>
                          )}
                        </div>
                        
                        {/* Feedback */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              Slide {slideFb.slide || slideFb.slide_number || index + 1}
                            </h4>
                          </div>
                          <div className="text-sm text-slate-600 space-y-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {slideFb.what_worked && (
                              <p><span className="text-emerald-600 font-medium">+</span> {slideFb.what_worked}</p>
                            )}
                            {slideFb.what_confused && (
                              <p><span className="text-amber-600 font-medium">-</span> {slideFb.what_confused}</p>
                            )}
                            {!slideFb.what_worked && !slideFb.what_confused && (
                              <p>{slideFb.feedback || slideFb.comment || 'No specific feedback for this slide.'}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Improvement Plan */}
          {getImprovementPlanText() && (
            <Card className="bg-white border-slate-200 mt-6">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Improvement Plan</CardTitle>
                <CardDescription style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Actionable steps to enhance your presentation skills
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-line" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {getImprovementPlanText()}
                </p>
              </CardContent>
            </Card>
          )}

        </div>
      </div>
    </MainLayout>
  );
};

export default EvolveFeedback;
